import {
  ComboBox,
  type ComboBoxProps,
  type ComboBoxSize,
  FormField,
  type FormFieldProps,
  type FormFieldSize,
  type SelectOption,
} from "@aurem/ui";
import { useCallback, useId } from "react";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import { get } from "../../utils";

type ComboBoxOmitProps = "value" | "onChange" | "multiple";

type BaseComboBoxControlProps = Partial<Omit<FormFieldProps, "size">> & {
  name: string;
  formElementClass?: string;
  clear?: boolean;
};

type SingleComboBoxControlProps = Omit<ComboBoxProps, ComboBoxOmitProps> &
  BaseComboBoxControlProps & {
    multiple?: false;
    onChange?: (value: SelectOption | undefined, item?: SelectOption) => void;
  };

type MultipleComboBoxControlProps = Omit<ComboBoxProps, ComboBoxOmitProps> &
  BaseComboBoxControlProps & {
    multiple: true;
    onChange?: (value: SelectOption[], item?: SelectOption) => void;
  };

type Props = SingleComboBoxControlProps | MultipleComboBoxControlProps;

export const ComboBoxControl = (props: Props) => {
  const {
    label,
    message,
    required,
    tooltipContent,
    accessory,
    formElementClass,
    name,
    invalid,
    onChange,
    clear,
    description,
    size = "md",
    multiple,
    ...restComboBoxProps
  } = props;
  const { control } = useFormContext();
  const { errors } = useFormState();
  const errorMessage = get(errors, name)?.message as string | undefined;
  const inputId = useId();

  const formFieldSize: Record<ComboBoxSize, FormFieldSize> = {
    sm: "sm",
    md: "md",
    lg: "lg",
  };

  const handleChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (onFieldChange: (...event: any[]) => void) => {
      return (
        value: SelectOption | SelectOption[] | undefined,
        item?: SelectOption,
      ) => {
        if (onChange) {
          onChange(value as never, item);
        }
        onFieldChange(value);
      };
    },
    [onChange, multiple],
  );

  const comboBoxElement = (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: onFieldChange, value } }) => {
        const handleClear = () => {
          const clearValue = multiple ? [] : undefined;
          onFieldChange(clearValue);
          if (onChange) {
            onChange(clearValue as never);
          }
        };

        return (
          <ComboBox
            {...restComboBoxProps}
            onChange={handleChange(onFieldChange)}
            value={value}
            invalid={invalid || !!errorMessage}
            inputId={inputId}
            size={size}
            multiple={multiple}
            onClear={
              restComboBoxProps.onClear || (clear ? handleClear : undefined)
            }
          />
        );
      }}
    />
  );

  return (
    <FormField
      label={label}
      message={message ?? errorMessage}
      description={description}
      required={required}
      tooltipContent={tooltipContent}
      className={formElementClass}
      accessory={accessory}
      htmlFor={inputId}
      size={formFieldSize[size]}
    >
      {comboBoxElement}
    </FormField>
  );
};
